import { InteractionType } from "@azure/msal-browser";
import type { AuthenticationConfig } from "@sdeapps/react-core";

const APPLICATION_NAME = "Contacts Métiers";
const DEFAULT_PAGE_TITLE = "Contacts Métiers";

export type RouteDefinition = {
  path: string;
  getParameterPath: (...parameters: Array<string>) => string;
  title: string;
};
/**
 * Clés des routes déclarées / déclarables.
 * La syntaxe `.. as const` permet d'avoir un tableau immuable (enum like)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const declaredRoutesKeys = ["home", "nouveauContactMetier", "saisieEnMasse", "any"] as const;
/** Type d'union des clés de routes possibles afin de typer nos `routesConfig` */
type RoutesConfigKey = (typeof declaredRoutesKeys)[number];
type RoutesConfig = Record<RoutesConfigKey, RouteDefinition>;
const routesConfig: RoutesConfig = {
  home: { path: "/", getParameterPath: (_) => "/", title: DEFAULT_PAGE_TITLE },
  nouveauContactMetier: {
    path: "/nouveau",
    getParameterPath: (_) => "/nouveau",
    title: "Nouveau Contact Métier",
  },
  saisieEnMasse: {
    path: "/saisie-en-masse",
    getParameterPath: (_) => "/saisie-en-masse",
    title: "Création en masse de Contacts Métiers",
  },
  any: { path: "*", getParameterPath: (_) => "*", title: DEFAULT_PAGE_TITLE },
};

export type RouteBaseObject = {
  path: string;
  element: string;
};
const routes: Array<RouteBaseObject> = Object.values(routesConfig).map(
  ({ path, title: element }) => ({ path, element })
);

const config = {
  basename: import.meta.env.PUBLIC_PATH,
  isBrowser: typeof window !== "undefined",
  appName: APPLICATION_NAME,
  appVersion: import.meta.env.VITE_VERSION ?? "local",
};

const authenticationConfig: AuthenticationConfig = {
  msal: {
    auth: {
      clientId: import.meta.env.VITE_AUTH_CLIENT_ID,
      authority: import.meta.env.VITE_AUTH_AUTHORITY,
      knownAuthorities: [import.meta.env.VITE_AUTH_AUTHORITY ?? ""],
      redirectUri: `${import.meta.env.VITE_APP_BASE_URL}/redirect.html`,
      postLogoutRedirectUri: `${import.meta.env.VITE_APP_BASE_URL}/redirect.html`,
    },
    cache: {
      cacheLocation: "localStorage",
    },
  },
  graph: {
    graphEndpoint: "https://graph.microsoft.com/v1.0",
    scopes: ["User.Read"],
  },
  interactionType: InteractionType.Redirect,
};

interface SDEApi {
  scopes: Array<string>;
  apiName: string;
}

const apiConfig: {
  apimBaseUrl: string;
  apimSubscriptionKey: string;
  cometApi: SDEApi;
  collectivitesApi: SDEApi;
  azureAd: {
    scopes: Array<string>;
    groupeTechniciens: string;
  };
} = {
  apimBaseUrl: import.meta.env.VITE_APIM_BASE_URL ?? "",
  apimSubscriptionKey: import.meta.env.VITE_APIM_SUBSCRIPTION_KEY ?? "",
  cometApi: {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    scopes: import.meta.env?.VITE_COMET_SCOPES?.split(" ") ?? [],
    apiName: import.meta.env.VITE_COMET_API_NAME ?? "",
  },
  collectivitesApi: {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    scopes: import.meta.env?.VITE_COLLECTIVITES_SCOPES?.split(" ") ?? [],
    apiName: import.meta.env.VITE_COLLECTIVITES_API_NAME ?? "",
  },
  azureAd: {
    scopes: ["User.Read"],
    groupeTechniciens: import.meta.env.VITE_OPALE_GROUPE_TECHNICIENS ?? "",
  },
};

const appiConfig: { connectionString: string } = {
  connectionString: import.meta.env.VITE_APPI_CONNECTION_STRING ?? "",
};

const envConfig: { name: string } = {
  name: import.meta.env.VITE_ENV ?? "",
};

const graphConfig: { graphEndpoint: string } = {
  graphEndpoint: "https://graph.microsoft.com/v1.0",
};

const localStorageKeys: Record<string, `sdeapps.comet.${string}`> = {
  isNavigationMenuOpen: "sdeapps.comet.isNavigationMenuOpen",
  listDensity: "sdeapps.comet.listDensity",
};

const COLLECTIVITES_APP_DOMAIN_NAME = import.meta.env.VITE_COLLECTIVITES_APP_DOMAIN_NAME ?? "";

export {
  config,
  DEFAULT_PAGE_TITLE,
  apiConfig,
  authenticationConfig,
  appiConfig,
  envConfig,
  routesConfig,
  routes,
  graphConfig,
  localStorageKeys,
  COLLECTIVITES_APP_DOMAIN_NAME,
};
